import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Pets as PetsIcon,
  Favorite as FavoriteIcon,
  CalendarToday as CalendarIcon,
  Notifications as NotificationsIcon,
  Message as MessageIcon,
  ShoppingCart as ShoppingCartIcon,
  HealthAndSafety as HealthIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  AccessTime as AccessTimeIcon,
  Store as StoreIcon,
} from '@mui/icons-material';

export const LandingPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleGetStarted = () => {
    // Rediriger vers l'application principale
    window.location.href = 'https://app.vetlink.fr';
  };

  const handleJoinPilot = () => {
    // Redirection temporaire vers /app (sera remplacé par un formulaire de contact)
    window.location.href = '/app';
  };

  const handleBookAppointment = () => {
    // Lien vers la prise de rendez-vous
    window.open('https://calendly.com/hichamboulaamani/30min', '_blank');
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header avec logo */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'primary.main' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'white',
              }}
            >
              <FavoriteIcon sx={{ fontSize: 32, color: 'white' }} />
              <PetsIcon sx={{ fontSize: 24, ml: -2.5, mt: 0.5, color: 'white' }} />
              <Typography variant="h5" sx={{ fontWeight: 600, ml: 1 }}>
                VetLink
              </Typography>
            </Box>
          </Box>
          <Button
            color="inherit"
            onClick={handleGetStarted}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
            }}
          >
            Accéder à l'app
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'center' }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 58.33%' }, maxWidth: { xs: '100%', md: '58.33%' } }}>
              <Typography
                variant={isMobile ? 'h4' : 'h3'}
                component="h1"
                gutterBottom
                sx={{ fontWeight: 700 }}
              >
                Connecting Vets and Pet Owners – Simply
              </Typography>
              <Typography
                variant="h6"
                sx={{ mb: 4, opacity: 0.9, fontWeight: 300 }}
              >
                A modern and friendly app designed to make veterinary care
                effortless.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleJoinPilot}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                    px: 4,
                    py: 1.5,
                  }}
                >
                  Rejoindre le programme pilote
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleBookAppointment}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                    px: 4,
                    py: 1.5,
                  }}
                >
                  Prendre rendez-vous
                </Button>
              </Box>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 41.67%' }, maxWidth: { xs: '100%', md: '41.67%' }, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <PetsIcon sx={{ fontSize: 120, opacity: 0.3 }} />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Section Vidéo */}
      <Box
        sx={{
          bgcolor: 'background.default',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              component="video"
              src="/video/v09_Vetlink Explainer Video.mp4"
              controls
              autoPlay
              loop
              muted
              playsInline
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: '900px' },
                height: 'auto',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              }}
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Section: Le Défi */}
        <Box sx={{ mb: 10 }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}
          >
            Le Défi
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
              <Card elevation={3}>
                <CardContent>
                  <List>
                    {[
                      'Veterinarians are busier than ever',
                      'Missed appointments',
                      'Communication gaps with clients',
                      'Time-consuming post-visit follow-ups',
                      'Low engagement outside consultations',
                    ].map((item, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <AccessTimeIcon color="error" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.100',
                  borderRadius: 2,
                  p: 4,
                }}
              >
                <HealthIcon sx={{ fontSize: 120, color: 'grey.400' }} />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Section: L'Opportunité */}
        <Box sx={{ mb: 10 }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}
          >
            L'Opportunité
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 66.67%' } }}>
              <Card elevation={3}>
                <CardContent>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Pet owners are looking for digital ways to connect with their vets."
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <StarIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="80% of pet owners use mobile apps daily" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <NotificationsIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Clients want reminders, health tracing & direct contact" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <HealthIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Find the right advice for their animals" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 33.33%' } }}>
              <Card
                elevation={3}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CardContent>
                  <Typography variant="h2" sx={{ fontWeight: 700, textAlign: 'center' }}>
                    80%
                  </Typography>
                  <Typography variant="h6" sx={{ textAlign: 'center', mt: 2 }}>
                    des propriétaires utilisent des apps mobiles quotidiennement
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>

        {/* Section: Présentation de VetLink */}
        <Box sx={{ mb: 10 }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}
          >
            Présentation de VetLink
          </Typography>
          <Typography
            variant="h5"
            sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}
          >
            Your all-in-one mobile app for better client engagement
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
            {[
              { icon: <HealthIcon />, title: 'Digital health records', color: '#2e7d32' },
              {
                icon: <CalendarIcon />,
                title: 'Appointment booking & reminders',
                color: '#1976d2',
              },
              { icon: <MessageIcon />, title: 'Direct messaging', color: '#ed6c02' },
              {
                icon: <ShoppingCartIcon />,
                title: 'Pet product marketplace (no logistics)',
                color: '#9c27b0',
              },
              {
                icon: <NotificationsIcon />,
                title: 'Notifications for clinic marketing & pet care',
                color: '#d32f2f',
              },
              {
                icon: <HealthIcon />,
                title: 'Advice sheets for their animals',
                color: '#00897b',
              },
            ].map((feature, index) => (
              <Card
                key={index}
                elevation={3}
                sx={{
                  height: '100%',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      color: feature.color,
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {React.cloneElement(feature.icon, { sx: { fontSize: 48 } })}
                  </Box>
                  <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Section: Pour les Vétérinaires */}
        <Box sx={{ mb: 10 }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}
          >
            Pour les Vétérinaires
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
            {[
              'Save time on reminders and follow-ups',
              'Strengthen client loyalty',
              'Increase recurring revenue',
              'Build a modern, digital brand',
              'Offer quality advice for pet owners',
              'And many future functionalities in progress!!',
            ].map((benefit, index) => (
              <Card key={index} elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircleIcon color="success" />
                    <Typography>{benefit}</Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Section: Pour les Propriétaires */}
        <Box sx={{ mb: 10 }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}
          >
            Pour les Propriétaires
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
            {[
              'Easy access to pet health info',
              'Reminders for treatments & visits',
              'Fun content to engage with their pet',
              'One-click access to their vet',
              'Quality advice for their pet',
            ].map((benefit, index) => (
              <Card key={index} elevation={2} sx={{ bgcolor: 'primary.light', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PetsIcon />
                    <Typography>{benefit}</Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Section: Marketplace Advantage */}
        <Box sx={{ mb: 10 }}>
          <Card
            elevation={5}
            sx={{
              bgcolor: 'secondary.main',
              color: 'white',
              p: 4,
            }}
          >
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}
            >
              The Marketplace Advantage
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3 }}>
              {[
                'Connect clients directly to your clinic\'s product recommendations',
                'No logistics or inventory',
                'You stay the trusted advisor',
                'Generate additional income effortlessly',
              ].map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <StoreIcon sx={{ fontSize: 32 }} />
                  <Typography variant="h6">{item}</Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Box>

        {/* Section: Exemples d'utilisation */}
        <Box sx={{ mb: 10 }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}
          >
            Exemples d'utilisation
          </Typography>
          <Card elevation={3}>
            <CardContent>
              <List>
                {[
                  'Making an appointment',
                  'Sending a reminder of the appointment',
                  'Update of the animal\'s health record',
                  'Sending advice following the consultation',
                  'Sent a few days after the consultation to know if the animal is well',
                  'Request for feedback from the owner on google review or social networks',
                ].map((item, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
              <Typography
                variant="h6"
                sx={{ mt: 3, textAlign: 'center', fontWeight: 600, color: 'primary.main' }}
              >
                All these actions are performed by the application and many others!!!
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Section: Programme Pilote */}
        <Box sx={{ mb: 10 }}>
          <Card
            elevation={5}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              p: 6,
            }}
          >
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}
            >
              Le Programme Pilote
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3, mb: 4 }}>
              {[
                'Free to join',
                'Lifetime access to premium features',
                'Dedicated onboarding & support',
                'Feedback helps shape the app',
              ].map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <StarIcon sx={{ fontSize: 32 }} />
                  <Typography variant="h6">{item}</Typography>
                </Box>
              ))}
            </Box>
            <Typography
              variant="h5"
              sx={{ textAlign: 'center', fontWeight: 600, mt: 4 }}
            >
              VetLink allows a strong link between vet and pet owners
            </Typography>
          </Card>
        </Box>

        {/* Call to Action */}
        <Box
          sx={{
            bgcolor: 'grey.900',
            color: 'white',
            p: 8,
            borderRadius: 4,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 700, mb: 3 }}
          >
            Join Our Pilot Program Today
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Let's build the future of pet care together.
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, opacity: 0.8 }}>
            Do you want to know more? Please make an appointment via this link.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleJoinPilot}
              sx={{
                bgcolor: 'white',
                color: 'grey.900',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                px: 6,
                py: 2,
                fontSize: '1.1rem',
              }}
            >
              Rejoindre maintenant
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleBookAppointment}
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                px: 6,
                py: 2,
                fontSize: '1.1rem',
              }}
            >
              Prendre rendez-vous
            </Button>
          </Box>
          <Typography
            variant="h6"
            sx={{ mt: 4, fontWeight: 600, color: 'primary.light' }}
          >
            Join now for free and get lifetime access to premium features!!!
          </Typography>
        </Box>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: 'grey.900',
          color: 'white',
          py: 4,
          mt: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <FavoriteIcon sx={{ fontSize: 24, color: '#1976d2' }} />
            <PetsIcon sx={{ fontSize: 20, ml: -1.5 }} />
            <Typography variant="h6" sx={{ ml: 1 }}>
              VetLink
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, opacity: 0.7 }}>
            © 2024 VetLink. Tous droits réservés.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};
